import PropTypes from 'prop-types';

import EDIT_TOOL_TYPES from '../data/edit-tool-types';
import EXAM_MODULES from '../data/exam-modules';
import EXAM_TYPES from '../data/exam-types';
import SEGMENTATION_LUNG_TYPES from '../data/segmentation-lung-types';
import SEX_OPTIONS from '../data/sex-options';
import USER_TYPES from '../data/user-types';

export const idTypes = PropTypes.oneOfType([
  PropTypes.oneOf([null]),
  PropTypes.string,
  PropTypes.number,
]);

export const userTypes = PropTypes.oneOf(Object.keys(USER_TYPES));

export const sexTypes = PropTypes.oneOf(Object.keys(SEX_OPTIONS));

export const examModules = PropTypes.oneOf(Object.keys(EXAM_MODULES));

export const examTypes = PropTypes.oneOf(Object.keys(EXAM_TYPES));

export const functionType = PropTypes.oneOfType([
  PropTypes.oneOf([null]),
  PropTypes.func,
]);

export const moduleShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
});

export const examTypeShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
});

export const companyShape = PropTypes.shape({
  cnpj: PropTypes.string,
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
});

export const doctorShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  institutions: PropTypes.arrayOf(companyShape),
  files: PropTypes.arrayOf(PropTypes.string),
  cpf: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string.isRequired,
  email: PropTypes.string,
  type: PropTypes.oneOf(['DOC']),
  phone: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.string,
    PropTypes.number,
  ]),
  inscription: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.string,
    PropTypes.number,
  ]),
  image: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.string]),
  access_to_exams: PropTypes.bool,
  access_to_report: PropTypes.bool,
  access_to_patient_data: PropTypes.bool,
  access_to_images: PropTypes.bool,
  accepts_usage_terms: PropTypes.bool,
  accepts_privacy_policy: PropTypes.bool,
});

export const technicianShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
  type: userTypes,
  inscription: PropTypes.string,
});

export const patientShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
  type: userTypes,
  inscription: PropTypes.string,
  sex: sexTypes,
  age: PropTypes.string,
  weight: PropTypes.number,
  height: PropTypes.number,
  bmi: PropTypes.number,
});

export const examShape = PropTypes.shape({
  comments: PropTypes.string,
  company: companyShape,
  date_created: PropTypes.string,
  date_requested: PropTypes.string,
  doctor: doctorShape,
  id: PropTypes.number.isRequired,
  image: PropTypes.string,
  modality: PropTypes.string,
  module: examModules,
  patient: patientShape,
  registry: PropTypes.string,
  status: PropTypes.string,
  technician: technicianShape,
  type: examTypes,
});

export const singleGlyphShape = PropTypes.shape({
  high: PropTypes.number,
  low: PropTypes.number,
  normal: PropTypes.number,
  percent: PropTypes.number,
});

export const glyphShape = PropTypes.shape({
  ll: singleGlyphShape,
  lu: singleGlyphShape,
  rl: singleGlyphShape,
  rm: singleGlyphShape,
  ru: singleGlyphShape,
});

export const histogramShape = PropTypes.shape({
  high: PropTypes.objectOf(PropTypes.number),
  low: PropTypes.objectOf(PropTypes.number),
  max: PropTypes.number,
  min: PropTypes.number,
  normal: PropTypes.objectOf(PropTypes.number),
});

export const imageShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  image: PropTypes.string,
  name: PropTypes.string,
  report: PropTypes.number,
  type: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.string]),
});

export const attenuationLowNormalOrHighShape = PropTypes.shape({
  LL: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.number]),
  LU: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.number]),
  RL: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.number]),
  RM: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.number]),
  RU: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.number]),
  classes: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.string])
  ),
  total: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.number]),
});

export const reportAttenuationShape = PropTypes.shape({
  high: attenuationLowNormalOrHighShape,
  low: attenuationLowNormalOrHighShape,
  normal: attenuationLowNormalOrHighShape,
});

export const reportShape = PropTypes.shape({
  attenuation: reportAttenuationShape,
  consolidation_g: PropTypes.number,
  consolidation_ml: PropTypes.number,
  cp_lo_g: PropTypes.number,
  cp_lo_ml: PropTypes.number,
  exam: examShape,
  ggo_g: PropTypes.number,
  ggo_ml: PropTypes.number,
  glyph: glyphShape,
  histogram: histogramShape,
  histogram_processed: histogramShape,
  id: PropTypes.number.isRequired,
  images: PropTypes.arrayOf(imageShape),
  normal: PropTypes.number,
  pulmonary_involvement_extent_ptlv: PropTypes.number,
  pulmonary_involvement_extent_tlv: PropTypes.number,
  pulmonary_involvement_severity_ptlw: PropTypes.number,
  pulmonary_involvement_severity_tlw: PropTypes.number,
  tlv: PropTypes.number,
  tlw: PropTypes.number,
  well_aerated: PropTypes.number,
});

export const justificationShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
  description: PropTypes.string,
});

export const singleExamPerformanceNumberShape = PropTypes.shape({
  all: PropTypes.number,
  approved: PropTypes.number,
  PropTypesjustifications: PropTypes.arrayOf(PropTypes.number),
  no_status: PropTypes.number,
  reproved: PropTypes.number,
  total: PropTypes.number,
});

export const examPerformanceNumbersShape = PropTypes.shape({
  all: singleExamPerformanceNumberShape,
  this_month: singleExamPerformanceNumberShape,
  today: singleExamPerformanceNumberShape,
});

export const examPerformanceShape = PropTypes.shape({
  tomographies: examPerformanceNumbersShape,
  radiographies: examPerformanceNumbersShape,
});

export const notificationShape = PropTypes.shape({
  date: PropTypes.string,
  id: PropTypes.number,
  message: PropTypes.string,
  url: PropTypes.string,
  user: PropTypes.number,
  viewed: PropTypes.bool,
});

export const userShape = PropTypes.shape({
  id: PropTypes.number,
  phone: PropTypes.string,
  name: PropTypes.name,
  inscription: PropTypes.string,
  image: PropTypes.string,
  files: PropTypes.arrayOf(PropTypes.object),
  email: PropTypes.string,
  date_joined: PropTypes.string,
  cpf: PropTypes.string,
  access_to_report: PropTypes.bool,
  access_to_patient_data: PropTypes.bool,
  access_to_images: PropTypes.bool,
  access_to_exams: PropTypes.bool,
  accepts_usage_terms: PropTypes.bool,
  accepts_privacy_policy: PropTypes.bool,
});

export const dicomImagesShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  jpeg: PropTypes.string,
});

export const examSerieShape = PropTypes.shape({
  dicomImages: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.oneOf([null]),
      PropTypes.string,
      dicomImagesShape,
    ])
  ),
  imagesCount: PropTypes.number,
  seriesDate: PropTypes.string,
  seriesDescription: PropTypes.string,
  seriesInstanceUID: PropTypes.string,
  uploaded: PropTypes.bool,
});

export const noSearchResultMessageShape = PropTypes.shape({
  firstMessage: PropTypes.string,
  secondMessage: PropTypes.string,
});

export const adminShape = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
});

export const institutionShape = PropTypes.shape({
  address: PropTypes.string,
  administrators: adminShape,
  city: PropTypes.string,
  cnpj: PropTypes.string,
  complement: PropTypes.string,
  country: PropTypes.string,
  exams: PropTypes.oneOfType([
    examPerformanceNumbersShape,
    PropTypes.shape({
      all: PropTypes.number,
      this_month: PropTypes.number,
      today: PropTypes.number,
    }),
  ]),
  headquarters: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.number,
  image: PropTypes.string,
  is_headquarters: PropTypes.bool,
  name: PropTypes.string,
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  post_code: PropTypes.string,
  units: PropTypes.number,
  users: PropTypes.arrayOf(PropTypes.number),
});

export const institutionPermissionsShape = PropTypes.shape({
  id: PropTypes.number,
  permission: PropTypes.string,
  institution: PropTypes.institution,
});

export const annotationTypeShape = PropTypes.shape({
  id: idTypes,
  name: PropTypes.string,
  color: PropTypes.string,
  parent: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.string,
    PropTypes.number,
  ]),
  subtypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: idTypes,
      name: PropTypes.string,
      color: PropTypes.string,
      parent: PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.string,
        PropTypes.number,
      ]),
    })
  ),
});

export const optionShape = PropTypes.shape({
  key: idTypes,
  text: PropTypes.string,
  value: idTypes,
});

export const segmentationShape = PropTypes.shape({
  id: idTypes,
  x: PropTypes.number,
  y: PropTypes.number,
  radius: PropTypes.number,
  type: PropTypes.oneOf([
    '',
    'eraser',
    ...Object.keys(SEGMENTATION_LUNG_TYPES),
  ]),
  z: PropTypes.number,
  original: idTypes,
  mask: idTypes,
  classificated: idTypes,
  impactedSlices: PropTypes.arrayOf(
    PropTypes.shape({
      original: idTypes,
      classificated: idTypes,
      mask: idTypes,
      radius: PropTypes.number,
    })
  ),
});

export const annotationShape = PropTypes.shape({
  slice: PropTypes.string,
  x: PropTypes.number,
  y: PropTypes.number,
  id: idTypes,
  type: annotationTypeShape,
});

export const actionShape = PropTypes.shape({
  id: idTypes,
  type: PropTypes.oneOfType([
    PropTypes.oneOf(['', ...Object.keys(EDIT_TOOL_TYPES)]),
    annotationTypeShape,
  ]),
  points: PropTypes.arrayOf(
    PropTypes.oneOfType([annotationShape, segmentationShape])
  ),
});
